import React from 'react';
import { useAppContext } from '../context/AppContext';
import { Globe, MapPin, Users, CheckCircle, ArrowRight, Clock, Shield, Zap, Target, Feather } from 'lucide-react';


/* How It Works — tightened spacing (no big gap) */
const HowItWorksFlow = () => (
  <section className="py-10"> {/* was py-20/24 */}
    <div className="container mx-auto px-4">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-6">How It Works</h2> {/* was mb-12/16 */}

      <div className="mx-auto w-full max-w-7xl">
        <svg
          viewBox="0 0 1600 700"   /* shorter canvas */
          className="w-full h-auto"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <marker id="arrowEnd" markerWidth="14" markerHeight="14" refX="10" refY="5" orient="auto">
              <path d="M0,0 L0,10 L12,5 z" fill="#475569" />
            </marker>
          </defs>

          {/* move everything UP: top line at y=120; cards start at y=180 */}
          {/* Top relationship */}
          <circle cx="360" cy="120" r="22" fill="#F97316" />
          <circle cx="1240" cy="120" r="22" fill="#F97316" />
          <line x1="360" y1="120" x2="1240" y2="120" stroke="#475569" strokeWidth="4" markerEnd="url(#arrowEnd)" />
          <text x="800" y="92" textAnchor="middle" fontSize="18" fill="#374151">
            Direct employer–employee relationship (day-to-day management, allocation, appraisals)
          </text>

          {/* Left card */}
          <rect x="180" y="180" width="360" height="200" rx="20" fill="#F8FAFC" stroke="#CBD5E1" />
          <text x="360" y="240" textAnchor="middle" fontSize="28" fontWeight="700" fill="#F97316">Company</text>
          <text x="360" y="270" textAnchor="middle" fontSize="18" fontWeight="700" fill="#334155">Team Management</text>
          <text x="360" y="300" textAnchor="middle" fontSize="15" fill="#334155">Compliance, taxes, payroll,</text>
          <text x="360" y="325" textAnchor="middle" fontSize="15" fill="#334155">talent infrastructure &amp; engagement</text>

          {/* Hub */}
          <rect x="620" y="180" width="360" height="200" rx="22" fill="#EEF2F7" stroke="#94A3B8" />
          <text x="800" y="250" textAnchor="middle" fontSize="40" fontWeight="800" fill="#0f172a">
            EXCESS<tspan fill="#ef4444">D</tspan>
          </text>
          <text x="800" y="280" textAnchor="middle" fontSize="16" fill="#475569">Bridge • Compliance • Operations</text>

          {/* Right card */}
          <rect x="1060" y="180" width="360" height="200" rx="20" fill="#F8FAFC" stroke="#CBD5E1" />
          <text x="1240" y="240" textAnchor="middle" fontSize="28" fontWeight="700" fill="#F97316">Professional</text>
          <text x="1240" y="270" textAnchor="middle" fontSize="18" fontWeight="700" fill="#334155">Local Employer-On-Record</text>
          <text x="1240" y="300" textAnchor="middle" fontSize="15" fill="#334155">Payroll, leave, benefits, taxes,</text>
          <text x="1240" y="325" textAnchor="middle" fontSize="15" fill="#334155">infrastructure &amp; HR/Finance support</text>

          {/* Curved flows under cards -> hub top (stay below titles) */}
          <path d="M360,380 C 360,520 800,520 800,180"
                fill="none" stroke="#475569" strokeWidth="4" markerEnd="url(#arrowEnd)" />
          <path d="M1240,380 C 1240,520 800,520 800,180"
                fill="none" stroke="#475569" strokeWidth="4" markerEnd="url(#arrowEnd)" />

          {/* Downstream responsibilities */}
          <line x1="800" y1="380" x2="800" y2="530" stroke="#475569" strokeWidth="4" markerEnd="url(#arrowEnd)" />
          <text x="800" y="565" textAnchor="middle" fontSize="20" fontWeight="700" fill="#334155">What We Handle</text>
          <text x="800" y="595" textAnchor="middle" fontSize="16" fill="#334155">
            Compliance • Contracts • Talent Ops • Onboarding • Engagement
          </text>
        </svg>
      </div>
    </div>
  </section>
);


const GatewayToIndiaPage = () => {
  const { setContactFormOpen } = useAppContext();

  const scrollToHowWeSimplify = () => {
    const howWeSimplifySection = document.getElementById('how-we-simplify-section');
    if (howWeSimplifySection) {
      howWeSimplifySection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 via-teal-800 to-blue-700 text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl">
            <div className="mb-6">
              {/* <span className="inline-block bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wide">
                Gateway to India
              </span> */}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Your Strategic Entry Point
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-90">
              We make it simple for companies to expand their engineering capacity in India without complexities of setting up local operations
            </p>
            {/* <p className="text-lg mb-12 leading-relaxed opacity-80">
              Leverage India's world-class talent pool while we handle all the operational complexities, compliance requirements for you.
            </p> */}
            
            <div className="flex flex-col sm:flex-row gap-4">
              {/* <button 
                onClick={() => setContactFormOpen(true)}
                className="bg-yellow-400 text-black font-bold py-4 px-8 rounded-md hover:bg-yellow-300 transition-colors inline-flex items-center justify-center group"
              >
                START YOUR JOURNEY
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={scrollToHowWeSimplify}
                className="border-2 border-white text-white font-bold py-4 px-8 rounded-md hover:bg-white hover:text-green-900 transition-colors"
              >
                Learn More
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Why India Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Why India?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto text-xl">
              India has emerged as the global hub for technology innovation and engineering excellence.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center border-t-4 border-blue-600">
              <div className="text-3xl font-bold text-blue-600 mb-2">4M+</div>
              <div className="text-gray-600 text-xl">IT Professionals</div>
              <p className="text-sm text-gray-500 mt-2 text-xl">Largest pool of English-speaking tech talent</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center border-t-4 border-green-600">
              <div className="text-3xl font-bold text-green-600 mb-2">Cost Savings</div>
              <div className="text-gray-600 text-xl"> </div>
              <p className="text-sm text-gray-500 mt-2 text-xl">Significant cost advantage without compromising quality</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center border-t-4 border-purple-600">
              <div className="text-3xl font-bold text-purple-600 mb-2">12+</div>
              <div className="text-gray-600 text-xl">Time Zone Advantage</div>
              <p className="text-sm text-gray-500 mt-2 text-xl">Suitable working hours for global collaboration</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center border-t-4 border-orange-600">
              <div className="text-3xl font-bold text-orange-600 mb-2">Top 3</div>
              <div className="text-gray-600 text-xl">Global Talent Hub</div>
              <p className="text-sm text-gray-500 mt-2 text-xl">Ranked among top destinations for IT Industry</p>
            </div>
          </div>
        </div>
      </section>

      {/* How We Simplify Section */}
      <section id="how-we-simplify-section" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">How We Simplify Your India Entry</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We eliminate all the complexities and barriers, making your expansion seamless and efficient.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold mb-6">Complete Operational Support</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-red-100 p-2 rounded-full mr-4 mt-1">
                    <Feather className="text-red-600" size={20} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">No Legal or Administrative Burden</h4>
                    <p className="text-gray-700">We handle all legal entity setup, compliance, taxation, and regulatory requirements.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-full mr-4 mt-1">
                    <Clock className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Rapid Team Ramp-up</h4>
                    <p className="text-gray-700">Rapidly operationalize your team with full transparency throughout the process.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-full mr-4 mt-1">
                    <Shield className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Protection and Security</h4>
                    <p className="text-gray-700">You own the Intellectual Property rights.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-purple-100 p-2 rounded-full mr-4 mt-1">
                    <Globe className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Cultural & Timezone Alignment</h4>
                    <p className="text-gray-700">Teams that understand your business culture and work in compatible time zones.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-lg h-96 overflow-hidden shadow-lg">
                <img 
                  src="/assets/images/gateway-india.jpg" 
                  alt="Gateway to India" 
                  className="w-full h-full object-cover"
                />
                {/* <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                  <div className="text-white p-6 w-full">
                    <p className="text-xl font-bold mb-2">Your Gateway to India</p>
                    <p className="text-sm opacity-90">Seamless Market Entry</p>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12">How It Works</h2>

          <img 
            src="/assets/images/how-it-works.png"   // place your uploaded image here
            alt="How It Works — EXCESSD"
            className="mx-auto w-full max-w-5xl rounded-lg shadow-md"
            loading="lazy"
          />
        </div>
      </section>



      {/* Our India Presence */}
      {/* <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-100 to-red-200 rounded-xl p-8 h-96 flex items-center justify-center">
                <div className="text-center">
                  <MapPin size={64} className="text-orange-600 mx-auto mb-4" />
                  <p className="text-orange-800 font-medium text-lg">Bengaluru Operations</p>
                  <p className="text-orange-600">India's Silicon Valley</p>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Strategic Location</h2>
              <p className="text-lg mb-6 text-gray-700">
                Based in Bengaluru, India's technology capital, we provide you with access to the country's largest concentration of skilled software professionals.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-lg text-gray-700">Prime location in India's tech hub</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-lg text-gray-700">Access to top-tier educational institutions</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-lg text-gray-700">Established IT infrastructure and ecosystem</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-lg text-gray-700">English-speaking professional environment</span>
                </div>
              </div>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="text-lg font-bold mb-2 text-blue-800">Our Address</h4>
                <p className="text-blue-700">
                  4th Floor, Vaswani Presidio<br />
                  Panathur Main Road<br />
                  Off Outer Ring Road, Kaverappa Layout<br />
                  Kodubeesenahalli, Bengaluru<br />
                  Karnataka - 560103, India
                </p>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Success Stories Preview */}
      {/* <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Success Stories</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See how we've helped companies successfully establish their presence in India.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-xl font-bold mb-4">Startup Scale-up</h3>
              <p className="text-gray-700 mb-4">
                Helped a US-based startup scale from 5 to 25 engineers in just 6 months, reducing development costs by 40%.
              </p>
              <div className="text-sm text-gray-500">
                <strong>Industry:</strong> FinTech
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-xl font-bold mb-4">Enterprise Expansion</h3>
              <p className="text-gray-700 mb-4">
                Enabled a Fortune 500 company to establish a 50-person R&D center in India within 3 months.
              </p>
              <div className="text-sm text-gray-500">
                <strong>Industry:</strong> Manufacturing
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-bold mb-4">Rapid Deployment</h3>
              <p className="text-gray-700 mb-4">
                Assembled a specialized AI/ML team for a European client in just 4 weeks, accelerating their product launch.
              </p>
              <div className="text-sm text-gray-500">
                <strong>Industry:</strong> Healthcare
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-green-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Enter the Indian Market?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Let us be your trusted partner - enabling you to innovate globally while building confidently in India.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setContactFormOpen(true)}
              className="bg-yellow-400 text-black font-bold py-4 px-8 rounded-md hover:bg-yellow-300 transition-colors inline-flex items-center justify-center group"
            >
              START CONVERSATION
              {/* <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" /> */}
            </button>
            {/* <button className="border-2 border-white text-white font-bold py-4 px-8 rounded-md hover:bg-white hover:text-blue-900 transition-colors">
              Download India Guide
            </button> */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GatewayToIndiaPage;